import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { View, StyleSheet, Pressable, Platform, Animated, RefreshControl } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useExploreStore } from '@/store';
import { trackEvent, trackPageView } from '@/constants/utils/analytics';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import AppDashboardFooter from '@/components/AppDashboardFooter';

const isWeb = Platform.OS === 'web';
const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
const CARD_GAP = 14;
const CARD_HEIGHT = 220;

// ── Card ────────────────────────────────────────────────────────────────────

const CategoryCard = React.memo(({ category, onPress, colors }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  const featuredItem = category.featuredItem;
  const hasImage = featuredItem?.cdnPath || featuredItem?.thumbnailPath;
  const itemCount = category.stats?.itemCount || 0;
  const accent = category.color || colors.accent_color_primary;

  return (
    <Pressable
      onPress={() => onPress(category)}
      onMouseEnter={isWeb ? () => setHovered(true) : undefined}
      onMouseLeave={isWeb ? () => setHovered(false) : undefined}
      style={[
        styles.card,
        {
          backgroundColor: colors.solid_color_secondary,
          borderColor: hovered ? accent : colors.border_color_primary,
        },
        isWeb && hovered && styles.cardHovered,
      ]}
    >
      {hasImage ? (
        <View style={styles.cardImageWrapper}>
          <Image
            source={{ uri: featuredItem.thumbnailPath || featuredItem.cdnPath }}
            style={[styles.cardImage, hovered && isWeb && styles.cardImageHovered]}
            contentFit="cover"
            placeholder={featuredItem.blurhash ? { blurhash: featuredItem.blurhash } : undefined}
            transition={300}
            priority="low"
          />
        </View>
      ) : (
        <LinearGradient
          colors={[accent, shade(accent, -0.25)]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.cardImagePlaceholder}
        >
          <Icon name={category.icon || 'shapes'} size={42} color="rgba(255,255,255,0.85)" />
        </LinearGradient>
      )}

      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.35)', 'rgba(0,0,0,0.85)']}
        locations={[0, 0.55, 1]}
        style={styles.cardGradient}
        pointerEvents="none"
      />

      <View style={styles.cardOverlay}>
        <View style={styles.topRow}>
          <View style={[styles.iconCircle, { backgroundColor: accent }]}>
            <Icon name={category.icon || 'shapes'} size={13} color="#fff" />
          </View>
          {isWeb && hovered && (
            <View style={styles.arrowBadge}>
              <Icon name="arrow-right" size={11} color="#fff" />
            </View>
          )}
        </View>

        <View style={styles.bottomBlock}>
          <Text size="m" weight="bold" color="white" numberOfLines={1} style={styles.cardTitle}>
            {category.name}
          </Text>
          {category.description ? (
            <Text size="xs" color="white" numberOfLines={1} style={styles.cardDescription}>
              {category.description}
            </Text>
          ) : null}

          <View style={styles.cardMeta}>
            {category.mediaTypes?.length > 0 && (
              <View style={styles.mediaTypePills}>
                {category.mediaTypes.map(mt => (
                  <View key={mt} style={styles.mediaTypePill}>
                    <Icon
                      name={mt === 'image' ? 'image' : mt === 'video' ? 'film' : 'music'}
                      size={9}
                      color="#fff"
                    />
                    <Text size="xxs" color="white" style={{ opacity: 0.95 }}>
                      {mt.charAt(0).toUpperCase() + mt.slice(1)}
                    </Text>
                  </View>
                ))}
              </View>
            )}
            {itemCount > 0 && (
              <Text size="xxs" color="white" style={{ opacity: 0.75 }}>
                {itemCount === 1
                  ? t('explore.categoryItemSingle')
                  : t('explore.categoryItems').replace('%COUNT%', itemCount.toLocaleString())}
              </Text>
            )}
          </View>
        </View>
      </View>
    </Pressable>
  );
});

CategoryCard.displayName = 'CategoryCard';

// ── Skeleton card (shimmering placeholder) ──────────────────────────────────

const SkeletonCard = React.memo(({ colors }) => {
  const pulse = useRef(new Animated.Value(0.5)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 700, useNativeDriver: !isWeb }),
        Animated.timing(pulse, { toValue: 0.5, duration: 700, useNativeDriver: !isWeb }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  return (
    <Animated.View
      style={[
        styles.card,
        styles.skeletonCard,
        {
          backgroundColor: colors.solid_color_secondary,
          borderColor: colors.border_color_primary,
          opacity: pulse,
        },
      ]}
    >
      <View style={[styles.skeletonBlock, { backgroundColor: colors.solid_color_tertiary }]} />
      <View style={styles.skeletonContent}>
        <View style={[styles.skeletonLine, { backgroundColor: colors.solid_color_tertiary, width: '60%' }]} />
        <View style={[styles.skeletonLine, { backgroundColor: colors.solid_color_tertiary, width: '85%', height: 10 }]} />
      </View>
    </Animated.View>
  );
});

SkeletonCard.displayName = 'SkeletonCard';

// ── Header ──────────────────────────────────────────────────────────────────

const ExploreHero = React.memo(({ colors, t, totalCategories, onBack }) => (
  <View style={styles.header}>
    <Pressable
      onPress={onBack}
      style={({ hovered }) => [
        styles.backLink,
        isWeb && hovered && { opacity: 0.65 },
      ]}
      hitSlop={8}
    >
      <Icon name="arrow-left" size={12} color={colors.text_color_primary} />
      <Text size="xs" weight="medium" color="primary" style={{ letterSpacing: 0.5 }}>
        {t('explore.backToExplore')}
      </Text>
    </Pressable>
  </View>
));

ExploreHero.displayName = 'ExploreHero';

// ── Screen ──────────────────────────────────────────────────────────────────

export default function ExploreCategoriesScreen() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { isXS, isSM } = useResponsiveness();

  const cachedCategories = useExploreStore(s => s.categoriesDetailed);
  const isLoading = useExploreStore(s => s.isCategoriesDetailedLoading);
  const fetchCategoriesDetailed = useExploreStore(s => s.actions.fetchCategoriesDetailed);

  const fadeAnim = useRef(new Animated.Value(cachedCategories.length ? 1 : 0)).current;
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    trackPageView('ExploreCategories');
    fetchCategoriesDetailed().then(() => {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 280,
        useNativeDriver: !isWeb,
      }).start();
    });
  }, [fetchCategoriesDetailed, fadeAnim]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchCategoriesDetailed({ force: true });
    } finally {
      setRefreshing(false);
    }
  }, [fetchCategoriesDetailed]);

  const numColumns = useMemo(() => {
    if (isXS) return 1;
    if (isSM) return 2;
    return 3;
  }, [isXS, isSM]);

  const handleCategoryPress = useCallback((category) => {
    trackEvent('explore', 'explore_category_select', category.slug);
    router.push(`/explore/category/${encodeURIComponent(category.slug)}`);
  }, [router]);

  const handleBack = useCallback(() => {
    trackEvent('explore', 'explore_categories_back');
    router.push('/explore');
  }, [router]);

  const nonMetaCategories = useMemo(
    () => cachedCategories.filter(c => c.slug !== 'trending' && c.slug !== 'new'),
    [cachedCategories],
  );

  const renderItem = useCallback(({ item }) => (
    <View style={styles.cellWrapper}>
      <CategoryCard category={item} onPress={handleCategoryPress} colors={colors} />
    </View>
  ), [handleCategoryPress, colors]);

  const showSkeletons = isLoading && nonMetaCategories.length === 0;

  const skeletonData = useMemo(
    () => Array.from({ length: numColumns * 3 }, (_, i) => ({ slug: `skeleton-${i}` })),
    [numColumns],
  );

  const renderSkeletonItem = useCallback(() => (
    <View style={styles.cellWrapper}>
      <SkeletonCard colors={colors} />
    </View>
  ), [colors]);

  const ListHeader = useMemo(() => (
    <ExploreHero
      colors={colors}
      t={t}
      totalCategories={nonMetaCategories.length}
      onBack={handleBack}
    />
  ), [colors, t, nonMetaCategories.length, handleBack]);

  const refreshControl = useMemo(() => {
    if (!isNative) return undefined;
    return (
      <RefreshControl
        refreshing={refreshing}
        onRefresh={handleRefresh}
        tintColor={colors.text_color_secondary}
      />
    );
  }, [refreshing, handleRefresh, colors]);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <MetaHead
        noindex
        title={t('explore.categoriesTitle')}
        description={t('explore.categoriesSubtitle')}
      />

      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {showSkeletons ? (
          <FlashList
            data={skeletonData}
            renderItem={renderSkeletonItem}
            estimatedItemSize={CARD_HEIGHT + CARD_GAP}
            keyExtractor={item => item.slug}
            numColumns={numColumns}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.innerContent}
            ListHeaderComponent={ListHeader}
          />
        ) : (
          <FlashList
            data={nonMetaCategories}
            renderItem={renderItem}
            estimatedItemSize={CARD_HEIGHT + CARD_GAP}
            keyExtractor={item => item.slug}
            numColumns={numColumns}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.innerContent}
            ListHeaderComponent={ListHeader}
            ListFooterComponent={<AppDashboardFooter fullViewportWidth />}
            refreshControl={refreshControl}
            ListEmptyComponent={
              !isLoading ? (
                <View style={styles.emptyState}>
                  <Icon name="compass" size={42} color={colors.text_color_tertiary} />
                  <Text size="m" weight="semiBold" color="secondary">
                    {t('explore.noCategoriesFound')}
                  </Text>
                </View>
              ) : null
            }
          />
        )}
      </Animated.View>
    </View>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

// Tiny color shader used to derive a darker companion color for placeholder
// gradients. Accepts hex (#rgb / #rrggbb) and falls back to the input on
// anything unparseable. `amount` is in [-1, 1]: negative darkens, positive
// lightens.
function shade(hex, amount) {
  if (typeof hex !== 'string') return hex;
  const m = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const num = parseInt(h, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const adj = (c) => {
    const next = amount >= 0
      ? c + (255 - c) * amount
      : c * (1 + amount);
    return Math.max(0, Math.min(255, Math.round(next)));
  };
  const toHex = (c) => c.toString(16).padStart(2, '0');
  return `#${toHex(adj(r))}${toHex(adj(g))}${toHex(adj(b))}`;
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    width: '100%',
  },
  innerContent: {
    flex: 1,
    width: '100%',
    maxWidth: 960,
    paddingHorizontal: 15,
  },

  // Header
  header: {
    gap: 8,
    paddingHorizontal: 5,
    marginBottom: 15,
    paddingTop: 0,
  },
  backLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingVertical: 5,
    ...(isWeb && { cursor: 'pointer', transition: 'opacity 0.15s ease' }),
  },

  // Card
  cellWrapper: {
    flex: 1,
    padding: CARD_GAP / 2,
  },
  card: {
    height: CARD_HEIGHT,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    position: 'relative',
    ...(isWeb ? {
      cursor: 'pointer',
      transition: 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), border-color 0.25s ease, box-shadow 0.25s ease',
    } : {}),
  },
  cardHovered: {
    transform: [{ translateY: -3 }],
    boxShadow: '0 14px 32px rgba(0,0,0,0.18)',
  },
  cardImageWrapper: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  cardImage: {
    ...StyleSheet.absoluteFillObject,
    ...(isWeb ? { transition: 'transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)' } : {}),
  },
  cardImageHovered: {
    transform: [{ scale: 1.08 }],
  },
  cardImagePlaceholder: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardGradient: {
    ...StyleSheet.absoluteFillObject,
  },
  cardOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'space-between',
    padding: 14,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  iconCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: isWeb ? '0 4px 10px rgba(0,0,0,0.25)' : undefined,
  },
  arrowBadge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottomBlock: {
    gap: 4,
  },
  cardTitle: {
    letterSpacing: -0.2,
    textShadow: isWeb ? '0 1px 2px rgba(0,0,0,0.4)' : undefined,
  },
  cardDescription: {
    opacity: 0.85,
  },
  cardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 6,
  },
  mediaTypePills: {
    flexDirection: 'row',
    gap: 5,
    flexWrap: 'wrap',
  },
  mediaTypePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 5,
  },

  // Skeleton
  skeletonCard: {
    padding: 14,
    justifyContent: 'flex-end',
    gap: 10,
  },
  skeletonBlock: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 70,
    borderRadius: 0,
  },
  skeletonContent: {
    gap: 8,
  },
  skeletonLine: {
    height: 14,
    borderRadius: 4,
  },

  // Empty
  emptyState: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
    gap: 12,
  },
});
